import styles from "./styles.module.scss"
import { useMemo } from "react"
import { observer } from "mobx-react-lite"
import {
  CabinGradeType,
  CruiseSailingCabinGradeDiscount,
  TripSearchCabinGradesScanStatus,
  TripSearchCabinsScanStatus,
  TripSearchResultCabinGrade,
} from "@cruisen/contract"
import { CruiseDetailsCard } from "../../../../cards/CruiseDetailsCard"
import { _t, formatCurrency } from "@cruisen/i18n"
import { makeAutoObservable } from "mobx"
import { getCabinScanRoomLayout } from "@cruisen/react-shared"
import { ErrorHandledImage } from "../../../../atoms/ErrorHandledImage"
import { AnimatedTabs } from "../../components/AnimatedTabs"
import { ImagePreviewModal } from "../../../../modals/ImagePreviewModal"
import { DiscountPriceDetails } from "../../../../atoms/DiscountPriceDetails"
import Tag from "../../../../atoms/Tag"

class CabinsCardState {
  chosenCabinType: CabinGradeType
  cabinGradeByType: Map<CabinGradeType, TripSearchResultCabinGrade>
  isImageZoomModalVisible: boolean
  showFullDescription: boolean

  constructor(
    cabinsScanStatus: TripSearchCabinsScanStatus,
    cabinGrades: TripSearchResultCabinGrade[],
    defaultCabinType: CabinGradeType,
  ) {
    const cabinGradeByType = new Map<CabinGradeType, TripSearchResultCabinGrade>()
    for (const cabinGrade of cabinGrades) {
      const currentCabinGrade = cabinGradeByType.get(cabinGrade.type)
      if (
        cabinGrade.minPricePP.price > 0 &&
        (cabinsScanStatus !== "completed" || cabinGrade.cabinsAvailable > 0) &&
        (!currentCabinGrade?.minPricePP.price ||
          currentCabinGrade.minPricePP.price <= 0 ||
          currentCabinGrade.minPricePP.price > cabinGrade.minPricePP.price ||
          // use cabin grade with description and image when price is the same
          (cabinGrade.description?.trim() &&
            cabinGrade.imageUrl?.trim() &&
            (!currentCabinGrade.imageUrl ||
              !currentCabinGrade.description ||
              currentCabinGrade.name === currentCabinGrade.description) &&
            currentCabinGrade.minPricePP.price === cabinGrade.minPricePP.price))
      ) {
        cabinGradeByType.set(cabinGrade.type, {
          ...cabinGrade,
          name: cabinGrade.name?.trim(),
          description: cabinGrade.description?.trim(),
          imageUrl: cabinGrade.imageUrl?.trim(),
          cabinsAvailable: (currentCabinGrade?.cabinsAvailable ?? 0) + cabinGrade.cabinsAvailable,
        })
      } else if (!currentCabinGrade) {
        cabinGradeByType.set(cabinGrade.type, { ...cabinGrade })
      } else if (currentCabinGrade && cabinGrade.cabinsAvailable) {
        currentCabinGrade.cabinsAvailable += cabinGrade.cabinsAvailable
      }
    }

    this.chosenCabinType = defaultCabinType
    this.cabinGradeByType = cabinGradeByType
    this.isImageZoomModalVisible = false
    this.showFullDescription = false

    makeAutoObservable(this)
  }

  setIsImageZoomModalVisible(value: boolean) {
    this.isImageZoomModalVisible = value
  }

  setChosenCabinType(value: CabinGradeType) {
    this.chosenCabinType = value
  }

  toggleShowFullDescription() {
    this.showFullDescription = !this.showFullDescription
  }
}

export const CabinsCard = observer(function CabinsCard({
  cabinGradeTypes,
  cabinGrades,
  cabinsScanStatus,
  cabinGradesScanStatus,
  cabinGradeDiscounts,
}: {
  cabinGradeTypes: CabinGradeType[]
  cabinGrades: TripSearchResultCabinGrade[]
  cabinsScanStatus: TripSearchCabinsScanStatus
  cabinGradesScanStatus: TripSearchCabinGradesScanStatus
  cabinGradeDiscounts?: Omit<CruiseSailingCabinGradeDiscount, "cruiseSailingId">[]
}) {
  const store = useMemo(
    () =>
      new CabinsCardState(
        cabinsScanStatus,
        cabinGrades,
        cabinGradeTypes.length > 0 ? cabinGradeTypes[0] : "inside",
      ),
    [cabinsScanStatus, cabinGradesScanStatus],
  )

  const cabinGradeItems = [
    { id: "inside", text: _t(`cabinTypes.inside`) },
    { id: "outside", text: _t(`cabinTypes.outside`) },
    { id: "balcony", text: _t(`cabinTypes.balcony`) },
    { id: "suite", text: _t(`cabinTypes.suite`) },
  ].reduce(
    (acc, cabinGrade) => {
      if (store.cabinGradeByType.has(cabinGrade.id as CabinGradeType)) {
        acc.push({
          ...cabinGrade,
          isDiscounted:
            cabinGradeDiscounts?.some((discount) => discount.cabinGradeType === cabinGrade.id) ||
            false,
        })
      }
      return acc
    },
    [] as Array<{ id: string; text: string; isDiscounted: boolean }>,
  )

  const currentCabinType = store.cabinGradeByType.get(store.chosenCabinType)
  const cabinGradeDiscount = cabinGradeDiscounts?.find(
    (item) => item.cabinGradeType === store.chosenCabinType,
  )

  if (!currentCabinType) {
    // @TODO an empty state for that cabin grades component when currentCabinType is not defined
    return <></>
  }

  return (
    <>
      <CruiseDetailsCard
        title={_t("cruiseDetailsScreen.cabins")}
        paddingEnabled={false}
        // testID="cruise-cabins-card"
        bottomPaddingEnabled={false}
      >
        <div className={styles.container}>
          <div className={styles.tabsContainer}>
            <AnimatedTabs
              chosenItem={store.chosenCabinType}
              items={cabinGradeItems}
              onPress={(itemId) => store.setChosenCabinType(itemId as CabinGradeType)}
            />
          </div>
          <div className={styles.divider}></div>
          <button
            className={styles.imageZoomModalButton}
            onClick={() => store.setIsImageZoomModalVisible(true)}
          >
            <div className={styles.tagsList}>
              {cabinGradeDiscount?.discountPP ? <Tag variant="priceDrop" /> : null}
            </div>
            <ErrorHandledImage
              uri={currentCabinType.imageUrl}
              className={styles.cabinImage}
              errorMessage={_t("cruiseDetailsScreen.cabinImageUnavailable")}
            />
          </button>
          <p className={styles.cabinTypeName}>{currentCabinType.name}</p>
          <p className={styles.cabinTypeDescription}>
            {!(currentCabinType.description.length > 120) || store.showFullDescription
              ? currentCabinType.description
              : `${currentCabinType.description.slice(0, 120)}...`}
          </p>
          <button
            className={styles.showMoreButton}
            onClick={() => store.toggleShowFullDescription()}
          >
            {store.showFullDescription ? "Show less" : "Show more"}
          </button>
          {cabinGradesScanStatus !== "skipped" && currentCabinType.minPrice > 0 ? (
            <>
              <div className={styles.divider}></div>
              <div className={styles.priceRow}>
                <p className={styles.startingFrom}>Estimated at</p>
                {cabinGradeDiscount?.discountPP ? (
                  <div className={styles.priceColumnEnd}>
                    <DiscountPriceDetails
                      alignRight
                      pricePP={{
                        ...currentCabinType.minPricePP,
                        discount: cabinGradeDiscount.discountPP,
                        discountPercent: cabinGradeDiscount.discountPercent,
                      }}
                    />
                  </div>
                ) : (
                  <div className={styles.priceColumn}>
                    <div className={styles.pricePPRow}>
                      <p className={styles.price}>
                        {formatCurrency(currentCabinType.minPricePP.price, 0, 0)}
                      </p>
                      <p className={styles.genericText}>{_t("cruiseDetailsScreen.perPerson")}</p>
                    </div>
                    <p className={styles.genericText}>
                      for {getCabinScanRoomLayout(currentCabinType.minPricePP)}
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </CruiseDetailsCard>
      {store.isImageZoomModalVisible ? (
        <ImagePreviewModal
          files={[
            {
              fileName: currentCabinType?.name,
              fileUrl: currentCabinType?.imageUrl,
            },
          ]}
          onClose={() => store.setIsImageZoomModalVisible(false)}
        />
      ) : null}
    </>
  )
})
